import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Badge,
  Button,
  Grid,
  Icon,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import DeleteIcon from "@mui/icons-material/Delete";
import { MathJax } from "better-react-mathjax";
import { hydrateHtmlWithResources } from "utils/resourceManager";

export default function Choices(props) {
  const { answers, updatedAnswer, answer_methods, classes, initialResources = [] } = props;
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    for (const menu of document.getElementsByClassName("tox-statusbar")) {
      menu.style.border = 0;
    }
    for (const menu of document.getElementsByClassName("tox-tinymce")) {
      menu.style.border = 0;
    }
  });

  useEffect(() => {
    answer_methods.change_visibility_menu(null);
  }, []);

  // Hydrate {{ img_tag(N) }} placeholders with actual images for editing
  useEffect(() => {
    if (initialResources && initialResources.length > 0 && answers.length > 0 && !hasHydratedRef.current) {
      let needsHydration = false;

      // Check if any choice needs hydration
      answers.forEach((answer) => {
        if (answer.text && /\{\{\s*img_tag\(/.test(answer.text)) {
          needsHydration = true;
        }
      });

      if (needsHydration) {
        answers.forEach((answer, index) => {
          if (answer.text && /\{\{\s*img_tag\(/.test(answer.text)) {
            const hydratedHtml = hydrateHtmlWithResources(answer.text, initialResources);
            if (hydratedHtml !== answer.text) {
              answer_methods.set_answer(index, hydratedHtml);
            }
          }
        });
        hasHydratedRef.current = true;
      }
    }
  }, [initialResources, answers]);

  return (
    <Grid container spacing={3}>
      {answers.map((answer, key) => (
        <Grid item xs={12}>
          <Badge
            badgeContent={"✓"}
            color="primary"
            invisible={!answer.is_correct_answer}
            style={{ width: "100%" }}
          >
            <div
              className={
                key === updatedAnswer
                  ? classes.questionFocus
                  : classes.questionUnfocus
              }
            >
              {key === updatedAnswer && (
                <div style={{ position: "relative", marginBottom: "-40px" }}>
                  <Stack direction="row" justifyContent="flex-end">
                    <Tooltip
                      title={
                        answer.fixed_position
                          ? "Unlock position"
                          : "Lock position"
                      }
                      style={{ zIndex: 10 }}
                    >
                      <IconButton
                        onClick={() => answer_methods.fix_position(key)}
                      >
                        {answer.fixed_position ? (
                          <Icon sx={{ color: "#3498DB" }}>lock_open</Icon>
                        ) : (
                          <Icon sx={{ color: "#3498DB" }}>push_pin</Icon>
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        answer.is_correct_answer
                          ? "Set as incorrect"
                          : "Set as correct"
                      }
                      style={{ zIndex: 10 }}
                    >
                      <IconButton
                        onClick={() => answer_methods.set_right_answer(key)}
                        disabled={answer.text === ""}
                      >
                        {answer.is_correct_answer ? (
                          <Icon sx={{ color: "#E74C3C" }}>dangerous</Icon>
                        ) : (
                          <Icon
                            sx={{
                              color: answer.text === "" ? "#ECF0F1" : "#1ABC9C",
                            }}
                          >
                            verified
                          </Icon>
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete answer" style={{ zIndex: 10 }}>
                      <IconButton
                        onClick={() => answer_methods.delete_answer(key)}
                      >
                        <DeleteIcon sx={{ color: "#E74C3C" }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </div>
              )}
              <div style={{ padding: "5px" }}>
                <MathJax>
                  <Editor
                    apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                    value={answers[key].text}
                    onEditorChange={(content) =>
                      answer_methods.set_answer(key, content)
                    }
                    onClick={() => answer_methods.change_visibility_menu(key)}
                    style={{ borderRadius: "10px" }}
                    id={"choices_editor" + key}
                    init={{
                      branding: false,
                      height: 100,
                      max_height: 500,
                      min_height: 100,
                      placeholder: "Write your answers here",
                      menubar: false,
                      inline: false,
                      skin: "borderless",
                      elementpath: false,
                      plugins: [
                        "advlist autolink lists link image imagetools charmap print preview anchor",
                        "searchreplace visualblocks fullscreen",
                        "insertdatetime media table paste help codesample",
                      ],
                      toolbar:
                        "undo redo | " +
                        "bold italic | " +
                        "outdent indent | bullist numlist | " +
                        "table image codesample",
                      font_formats: "Roboto=roboto",
                      content_style:
                        "@import url('https://fonts.googleapis.com/css2?family=Roboto&display=swap'); body { font-family: 'Roboto', sans-serif; color: #092C4C }",
                      invalid_elements:
                        "script,iframe,object,embed,form,input,style,link,base,meta",
                      extended_valid_elements:
                        "pre[class|id|data-language|data-allow-edit|data-allow-copy|data-allow-paste],img[src|alt|title|width|height|data-resource-id]",
                      draggable_modal: true,
                      image_title: true,
                      automatic_uploads: true,
                      image_dimensions: false,
                      file_picker_types: "image",
                      file_picker_callback: function (cb, value, meta) {
                        var input = document.createElement("input");
                        input.setAttribute("type", "file");
                        input.setAttribute("accept", "image/*");
                        input.onchange = function () {
                          var file = this.files[0];
                          var reader = new FileReader();
                          reader.onload = function () {
                            var id = "blobid" + new Date().getTime();
                            var blobCache =
                              window.tinymce.activeEditor.editorUpload
                                .blobCache;
                            var base64 = reader.result.split(",")[1];
                            var blobInfo = blobCache.create(id, file, base64);
                            blobCache.add(blobInfo);
                            // Use data URI instead of blob URI so images persist
                            cb(reader.result, { title: file.name });
                          };
                          reader.readAsDataURL(file);
                        };
                        input.click();
                      },
                    }}
                  />
                </MathJax>
              </div>
            </div>
          </Badge>
        </Grid>
      ))}
      <Grid item xs={12}>
        <div className={classes.questionButton}>
          <Button
            fullWidth
            sx={{ textTransform: "none" }}
            onClick={() => answer_methods.add_answer()}
            disabled={answers.some((answer) => answer.text === "")}
          >
            Add choice
          </Button>
        </div>
      </Grid>
      <Grid item xs={12}>
        {answers.length <= 1 && (
          <>
            <Alert severity="error">
              You must provide at least two choices!
            </Alert>
            <br />
          </>
        )}
        {!answers.some((answer) => answer.is_correct_answer) && (
          <>
            <Alert severity="error">
              You must mark at least one choice as correct answer!
            </Alert>
            <br />
          </>
        )}
      </Grid>
    </Grid>
  );
}

Choices.propTypes = {
  answers: PropTypes.array.isRequired,
  updatedAnswer: PropTypes.number.isRequired,
  answer_methods: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  initialResources: PropTypes.array,
};
